import React, { useEffect } from 'react';
import { Card, Descriptions, Tag, Button, Space, Popconfirm, message, Table } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { DownloadOutlined, ShareAltOutlined } from '@ant-design/icons';
import { deleteOrder } from '../../store/ordersSlice';
import { fetchShops } from '../../store/shopsSlice';
import { formatCurrency } from '../../utils/formatters';
import { generateInvoicePDF } from '../../utils/invoiceGenerator';

import { API_URL } from '../../utils/api';

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orders } = useSelector((state) => state.orders);
  const { shops } = useSelector((state) => state.shops);
  const { user } = useSelector((state) => state.auth);
  const [invoiceLoading, setInvoiceLoading] = React.useState(false);
  
  useEffect(() => {
    dispatch(fetchShops());
  }, [dispatch]);

  const order = orders.find(o => o.id === orderId);

  const handleInvoice = async (action) => {
    try {
      setInvoiceLoading(true);
      await generateInvoicePDF(order, shop, bde, action);
      if (action === 'download') {
        message.success('Invoice downloaded successfully');
      }
    } catch (error) {
      message.error('Failed to generate invoice: ' + error.message);
    } finally {
      setInvoiceLoading(false);
    }
  };

  if (!order) {
    return <Card title="Order Details">Order not found</Card>;
  }

  const shop = shops.find(s => s.id === order.shopId);
  // Fallback to current user if BDE list is not available, assuming BDE views their own orders
  const bde = user; 

  const statusColor =
    order.status === 'Completed' ? 'green' :
      order.status === 'Pending' ? 'orange' :
        order.status === 'Processing' ? 'blue' :
          order.status === 'Cancelled' ? 'red' :
            order.status === 'Expected' ? 'gold' : 'default';

  const showInvoiceActions = ['Expected', 'Processing', 'Completed'].includes(order.status);

  return (
    <Card
      title={`Order Details: ${order.id}`}
      extra={
        <Space>
          {showInvoiceActions && (
            <>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={() => handleInvoice('download')}
                loading={invoiceLoading}
                disabled={!shop}
              >
                Invoice
              </Button>
              <Button 
                icon={<ShareAltOutlined />} 
                onClick={() => handleInvoice('share')}
                loading={invoiceLoading}
                disabled={!shop}
              >
                Share
              </Button>
            </>
          )}
          <Button onClick={() => navigate('/bde/orders')}>Back</Button>
          <Popconfirm
            title="Delete the order"
            description="Are you sure to delete this order?"
            onConfirm={async () => {
              try {
                await dispatch(deleteOrder(order.id)).unwrap();
                message.success('Order deleted');
                navigate('/bde/orders');
              } catch (err) {
                message.error('Failed to delete order: ' + err);
              }
            }}
            okText="Yes"
            cancelText="No"
            disabled={order.status === 'Completed' || order.status === 'Expected'}
          >
            <Button danger disabled={order.status === 'Completed' || order.status === 'Expected'}>Delete</Button>
          </Popconfirm>
        </Space>
      }
    >
      <Descriptions bordered column={1} style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Order ID">{order.id}</Descriptions.Item>
        <Descriptions.Item label="BDE ID">{order.bdeId}</Descriptions.Item>
        <Descriptions.Item label="Customer Name">{order.customerName}</Descriptions.Item>
        <Descriptions.Item label="Amount">{formatCurrency(order.amount)}</Descriptions.Item>
        <Descriptions.Item label="Status"><Tag color={statusColor}>{order.status}</Tag></Descriptions.Item>
        <Descriptions.Item label="Created At">{order.createdAt}</Descriptions.Item>
        <Descriptions.Item label="Payment Proof">
            {order.paymentProof ? (
                <a href={order.paymentProof.startsWith('http') ? order.paymentProof : `${API_URL}${order.paymentProof}`} target="_blank" rel="noopener noreferrer">
                    View Proof
                </a>
            ) : 'Not uploaded'}
        </Descriptions.Item>
        <Descriptions.Item label="Note">{order.items && order.items.length > 0 ? `${order.items.length} Items` : 'Single Item (Legacy)'}</Descriptions.Item>
      </Descriptions>

      <Table
        dataSource={order.items && order.items.length > 0 ? order.items : [{
          productName: order.productName,
          brandName: order.brandName,
          quantity: order.quantity,
          volumeMl: order.volumeMl,
          mrp: order.mrp,
          amount: order.billingPrice
        }]}
        rowKey={(record, index) => index}
        pagination={false}
        columns={[
          { title: 'Brand', dataIndex: 'brandName', key: 'brandName' },
          { title: 'Product', dataIndex: 'productName', key: 'productName' },
          { title: 'Volume', dataIndex: 'volumeMl', key: 'volumeMl', render: (val) => val ? `${val} ml` : '-' },
          { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
          { title: 'MRP', dataIndex: 'mrp', key: 'mrp', render: (val) => formatCurrency(val || 0) },
          { title: 'Total', dataIndex: 'amount', key: 'amount', render: (val) => formatCurrency(val || 0) },
        ]}
      />
    </Card>
  );
};

export default OrderDetails;
